import { t } from '@/i18n';

import { DATA_UNAVAILABLE_CODE, MAINTENANCE_CODE } from './data-availability';
import { extractErrorCode, extractErrorMessage, extractErrorStatus } from './error-handler';

/**
 * TRAD-2187: 서버 ErrorCode → 클라 로컬라이즈 i18n 키 매핑.
 *
 * 팀 규칙(글로벌 서비스 i18n): 에러는 code 기반 로컬라이즈. 서버가 안정적인 code 를 내려주고
 * 클라가 그 code 로 로케일별 문구를 표시한다. 매핑이 없으면 서버 message 로 폴백한다
 * (서버가 Accept-Language 로 이미 로컬라이즈한 message 를 내려주므로 안전한 폴백).
 *
 * 여기에 t()(로케일 의존)를 두는 파일은 shared/api 배럴에 export 하지 않는다 —
 * SharedWorker 등 window/localStorage 없는 컨텍스트로 t 모듈이 끌려가지 않게 하기 위함.
 */
// 값은 반드시 유효한 i18n 키여야 하므로 t()의 키 타입으로 고정한다(오타/미존재 키 컴파일 차단).
type I18nKey = Parameters<typeof t>[0];

const ERROR_CODE_I18N: Record<string, I18nKey> = {
  // TRAD-3861/3860 — 쿠키 키를 쓴 재발급·로그아웃에 X-Requested-With 가 없음(403). 정상 web 경로에선 0건 — 나오면 인터셉터 회귀 신호.
  AUTH_014: 'auth.error.csrf_header_required',
  // TRAD-3867 — 금융 콘텐츠 없음/비공개/삭제(계약 v2 §1-2).
  FINANCE_CONTENT_001: 'finance_content.detail.not_found',
  // 계약 v3 — relations 서버 검증 실패(400, 어드민 등록 경로 — web 공개 화면엔 안 오지만 코드 표엔 올린다).
  FINANCE_CONTENT_002: 'finance_content.error.invalid_relation',
  // TRAD-3904: 신고 제출이 닿는 코드 2개(문장은 iOS 와 같다). 서버 message 는 어조가 달라 매핑한다.
  USER_REPORT_002: 'report.error.target_not_found',
  USER_REPORT_003: 'report.error.already_reported',
  // TRAD-3556: 지도 bounds 조회가 서버 상한(statement_timeout 50s)을 넘김(503). 서버 message 는 한국어라 매핑이 없으면 EN 에 한글이 나간다.
  REAL_ESTATE_008: 'real_estate.map.error.query_timeout',
  // TRAD-3609: 관심 단지 추가·삭제 실패 3종 — HTTP 는 셋 다 404 라 code 로만 갈린다(backend RealEstateWatchlistErrorCode). 문장은 iOS 와 같다.
  REAL_ESTATE_009: 'real_estate.watchlist.error.already_registered',
  REAL_ESTATE_010: 'real_estate.watchlist.error.limit_exceeded',
  REAL_ESTATE_011: 'real_estate.watchlist.error.not_registered',
  // 인증 시도 횟수 초과(429). rate limit(TRAD-2170).
  AUTH_012: 'error.auth.too_many_attempts',
  // TRAD-2698: 투표 하루 1회 제한. 핵심 제약이라 거의 매일 발생하는데도 매핑이 비어 있었다.
  VOTE_001: 'vote.submit.error.already_voted',
  // 수집 실패로 지금 데이터를 줄 수 없음(503). 재시도하면 될 수 있다 (TRAD-2273/2281).
  [DATA_UNAVAILABLE_CODE]: 'error.data_unavailable.desc',
  // 계획된 점검(503). 재시도를 유도하지 않는다 (TRAD-2151/2152).
  [MAINTENANCE_CODE]: 'error.maintenance.desc',

  // ── 예측 마켓 제안 검증 (TRAD-2371) ────────────────────────────────────────
  // 🔴 backend 가 400 을 코드 11종으로 세분화하면서 **서버 message 를 영어로** 바꿨다
  // (`docs/error-codes.md` 정본, KO 문구는 클라 카탈로그가 갖는다). 그래서 매핑을 안 늘리면
  // 한국어 사용자에게 영어 문장이 그대로 뜬다 — 이 맵이 그 자리를 메운다.
  // 🔴 어느 칸이 잘못됐는지는 서버가 알려주지 않는다(detail 제거) — 사유만 code 로 온다.
  PROPOSAL_TITLE_REQUIRED: 'proposal.submit.error.title_required',
  PROPOSAL_CLOSE_AT_REQUIRED: 'proposal.submit.error.close_at_required',
  PROPOSAL_CLOSE_AT_PAST: 'proposal.submit.error.close_at_past',
  PROPOSAL_PRICE_FIELDS_REQUIRED: 'proposal.submit.error.price_fields_required',
  PROPOSAL_QUOTE_CURRENCY_UNSUPPORTED: 'proposal.submit.error.quote_currency_unsupported',
  PROPOSAL_ASSET_UNSUPPORTED: 'proposal.submit.error.asset_unsupported',
  PROPOSAL_TARGET_PRICE_INVALID: 'proposal.submit.error.target_price_invalid',
  PROPOSAL_SOURCE_URL_REQUIRED: 'proposal.submit.error.source_url_required',
  PROPOSAL_SOURCE_URL_INVALID: 'proposal.submit.error.source_url_invalid',
  PROPOSAL_JUDGMENT_CRITERIA_REQUIRED: 'proposal.submit.error.judgment_criteria_required',
  PROPOSAL_EXCEPTION_POLICY_REQUIRED: 'proposal.submit.error.exception_policy_required',

  // 자격 실패(등급·한도) — 종전엔 화면(ProposalFormPage)의 if 체인에 흩어져 있었다.
  // 같은 성격의 매핑을 두 곳에서 관리하면 한쪽만 늘어난다(TRAD-2371 에서 실제로 그럴 뻔했다).
  PROPOSAL_GRADE_REQUIRED: 'proposal.submit.error.grade_required',
  DAILY_PROPOSAL_LIMIT_EXCEEDED: 'proposal.submit.error.daily_limit',
  // TRAD-2698: 대기 중인 제안 3개 초과. PROPOSAL_* 11종과 같은 흐름인데 매핑이 빠져 있었다.
  PROPOSAL_PENDING_LIMIT_EXCEEDED: 'proposal.submit.error.pending_limit_exceeded',

  // TRAD-2413: 닉네임 정제 후 빈 문자열(태그만 등) → MyPageErrorCode.INVALID_NICK_NAME(400).
  // 서버 message 가 영어라 매핑이 없으면 KO 사용자에게 영어 문장이 뜬다. NickNameChange 가 localizeError 로 표시.
  MY_PAGE_002: 'mypage.nickname.invalid',

  // TRAD-3687: 내 자산(CryptoAssetErrorCode). 3사 문구 통일(TRAD-3687 코멘트 · iOS 카탈로그 원문 그대로). 매핑이 없으면
  // 서버 message(한 언어)가 그대로 떴다. CA_004 는 TRAD-3684 에서 코인원 등록·거래소 변경 시 상장 검증으로 처음 실제로 밟힌다.
  CA_001: 'portfolio.asset.error.already_registered',
  CA_002: 'portfolio.asset.error.not_found',
  CA_004: 'portfolio.asset.error.not_listed_on_exchange',

  // TRAD-3759(W4): 동의 일괄 기록(backend TermsErrorCode, TRAD-3756). TERMS_004 = 화면을 띄운 사이 새 버전 활성화(409) —
  // 재동의 화면은 이 code 를 받으면 목록을 다시 불러 다시 보여 준다. TERMS_005 = 서버가 모르는 종류(400).
  TERMS_004: 'consent.error.version_changed',
  TERMS_005: 'consent.error.unknown_type',
  // TERMS_006 = 활성 약관 없음(404) — «보기»(/terms·/privacy-policy 원문)를 열었는데 게시된 문서가 없을 때. 권유 문장을 붙이지 않는다(S27).
  TERMS_006: 'consent.error.document_unavailable',
  // TERMS_007 = 필수 미동의 회원의 쓰기(403, backend TRAD-3767 G2) — 공통 응답 처리가 동의 화면으로도 보낸다(G7, consent-required.ts). S30.
  TERMS_007: 'consent.error.consent_required',
  // TERMS_008 = 하우스 룰 미동의 회원의 글·댓글 쓰기(403, backend TRAD-3774) — 공통 응답 처리가 하우스 룰 다이얼로그도 연다
  // (TRAD-3777, consent-required.ts). 토스트 문구는 그 다이얼로그 설명과 같은 문장(새 문안 없음).
  TERMS_008: 'consent.house_rule_gate.body',

  // TRAD-3798: 업로드 용량 초과(413, backend TRAD-3795 · docs/error-codes.md E_007). 서버 message 는 «파일 용량이 너무 커서 올릴 수 없습니다.»라
  // 화면 맥락(사진)과 다르고 한 언어로만 온다. 아래 localizeError 의 «413 + code 없음» 폴백도 같은 키를 쓴다.
  E_007: 'upload.error.too_large',
};

/** TRAD-3798 — «HTTP 413 + JSON 아님»(nginx·프록시가 먼저 자른 HTML 413) 폴백 문구 키. E_007 과 같은 문구. */
const PAYLOAD_TOO_LARGE_FALLBACK_KEY: I18nKey = 'upload.error.too_large';

/** code → i18n 키(없으면 undefined). 문구 키의 정본은 위 표 하나다 — 화면이 같은 문구를 다른 조건(예: 클라 타임아웃)에도 쓰려면 이 함수로 키를 받아 간다. */
export const errorCodeI18nKey = (code: string | null | undefined): I18nKey | undefined =>
  code ? ERROR_CODE_I18N[code] : undefined;

/**
 * 에러에서 code 를 뽑아 매핑된 로컬라이즈 문구를 돌려준다. 매핑이 없으면 서버 message,
 * 그것도 없으면 fallback.
 */
export const localizeError = (error: unknown, fallback: string): string => {
  const code = extractErrorCode(error);
  const key = code ? ERROR_CODE_I18N[code] : undefined;
  if (key) {
    return t(key);
  }
  // TRAD-3798 — 413 인데 code 가 없다 = 서버(JSON)가 아니라 앞단(nginx·프록시)이 HTML 로 자른 것. 그대로 두면 axios 의
  // «Request failed with status code 413»(영어 개발 문구)이 토스트에 뜬다(extractErrorMessage → error.message).
  // 🔴 «code 없음»일 때만이다 — 413 + 다른 JSON code 는 종전대로 매핑 → 없으면 서버 message(android 와 같은 규칙).
  if (!code && extractErrorStatus(error) === 413) {
    return t(PAYLOAD_TOO_LARGE_FALLBACK_KEY);
  }
  return extractErrorMessage(error, fallback);
};
